import FingerprintJS from '@fingerprintjs/fingerprintjs'

// Cache the fingerprint agent to avoid re-initializing
let fingerprintAgent: any = null

/**
 * Generate an anonymous fingerprint for voting
 * Uses FingerprintJS to create a consistent identifier without storing personal data
 */
export async function generateVoteFingerprint(): Promise<string> {
  try {
    // Initialize fingerprint agent if not already done
    if (!fingerprintAgent) {
      fingerprintAgent = await FingerprintJS.load()
    }

    // Get fingerprint result
    const result = await fingerprintAgent.get()

    // Return visitor ID as the fingerprint
    // This is a stable identifier that doesn't change between sessions on the same device/browser
    return result.visitorId
  } catch (error) {
    console.error('Failed to generate fingerprint:', error)

    // Fallback: Generate a stable hash from browser characteristics
    // Using properties that don't change during a session
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width.toString(),
      screen.height.toString(),
      screen.colorDepth.toString(),
      new Date().getTimezoneOffset().toString(),
      navigator.hardwareConcurrency?.toString() || '0',
      navigator.platform
    ]

    const fallback = components.join('|')
    let hash = 0
    for (let i = 0; i < fallback.length; i++) {
      const char = fallback.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }

    // Prefix and pad the unsigned 32-bit hash so every fallback is long enough
    // for the voting and reporting API validation without exposing the source
    // browser characteristics.
    return `fallback-${(hash >>> 0).toString(36).padStart(7, '0')}`
  }
}

/**
 * Check if a fingerprint has already voted on a specific photo
 */
export async function hasUserVoted(photoId: string, fingerprint: string): Promise<boolean> {
  try {
    const { createServerSupabaseClient } = await import('@/lib/supabase')
    const supabase = createServerSupabaseClient()

    const { data, error } = await supabase
      .from('lawn_photo_votes')
      .select('id')
      .eq('photo_id', photoId)
      .eq('voter_fingerprint', fingerprint)
      .single()

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      console.error('Error checking vote:', error)
      return false
    }

    return !!data
  } catch (error) {
    console.error('Failed to check vote status:', error)
    return false
  }
}

/**
 * Get user's vote type for a photo (if any)
 */
export async function getUserVote(photoId: string, fingerprint: string): Promise<'upvote' | 'downvote' | null> {
  try {
    const { createServerSupabaseClient } = await import('@/lib/supabase')
    const supabase = createServerSupabaseClient()

    const { data, error } = await supabase
      .from('lawn_photo_votes')
      .select('vote_type')
      .eq('photo_id', photoId)
      .eq('voter_fingerprint', fingerprint)
      .single()

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      console.error('Error getting vote:', error)
      return null
    }

    return data?.vote_type || null
  } catch (error) {
    console.error('Failed to get user vote:', error)
    return null
  }
}






